import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users, Package, DollarSign, TrendingUp, TrendingDown, AlertCircle,
  Building2, UserCheck, Wallet, CreditCard
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import UpcomingDueCustomers from './UpcomingDueCustomers';
import GreetingHeader from './GreetingHeader';
import InstallmentSummaryCards from './InstallmentSummaryCards';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sea green + gold palette
const PALETTE = ['#22C55E', '#F59E0B', '#3B82F6', '#EF4444', '#A855F7', '#14B8A6'];

const chartTooltip = { contentStyle: { background: '#0F172A', border: '1px solid #1F2937', borderRadius: 4, color: '#F1F5F9' } };

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [profit, setProfit] = useState(null);
  const [payments, setPayments] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [salesExecs, setSalesExecs] = useState([]);
  const [filters, setFilters] = useState({
    date_from: '', date_to: '', branch_id: '', product_type: '', sales_executive_id: ''
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [s, f, inv, ins, p, br, ex] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/finance/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/inventory`, { withCredentials: true }),
        axios.get(`${API_URL}/api/installments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/super-admin-profit`, { withCredentials: true }),
        axios.get(`${API_URL}/api/branches`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users?role=Sales%20Executive`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setFinance(f.data); setProfit(p.data);
      setInventory(inv.data || []); setInstallments(ins.data || []);
      setBranches(br.data || []); setSalesExecs(ex.data || []);
      // Pull payments across installments (top 30 for chart)
      const pmts = [];
      for (const i of (ins.data || []).slice(0, 30)) {
        try {
          const r = await axios.get(`${API_URL}/api/installments/payments/${i.id}`, { withCredentials: true });
          pmts.push(...(r.data || []));
        } catch { /* skip */ }
      }
      setPayments(pmts);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const applyProfitFilters = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/super-admin-profit`,
        { params, withCredentials: true });
      setProfit(data);
    } catch { /* ignore */ }
  };

  const resetProfitFilters = async () => {
    setFilters({ date_from: '', date_to: '', branch_id: '', product_type: '', sales_executive_id: '' });
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/super-admin-profit`, { withCredentials: true });
      setProfit(data);
    } catch { /* ignore */ }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="text-slate-400">Loading dashboard...</div></div>;
  }

  // === Aggregations for charts ===
  // Collection trend by day (last 30 days)
  const days = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  const dayMap = Object.fromEntries(days.map(d => [d, 0]));
  payments.forEach(p => {
    const key = (p.payment_date || p.paid_date || '').slice(0, 10);
    if (key in dayMap) dayMap[key] += p.amount || 0;
  });
  const collectionSeries = days.map(d => ({ date: d.slice(5), collected: dayMap[d] }));

  // Portfolio pie — inventory by category
  const cat = {};
  inventory.forEach(i => { cat[i.product_type || 'Other'] = (cat[i.product_type || 'Other'] || 0) + 1; });
  const portfolio = Object.entries(cat).map(([name, value]) => ({ name, value }));

  // Overdue bar — installments by status
  const statusMap = { active: 0, defaulted: 0, completed: 0 };
  installments.forEach(i => { statusMap[i.status] = (statusMap[i.status] || 0) + 1; });
  const overdueBars = [
    { label: 'Active', count: statusMap.active || 0 },
    { label: 'Defaulted', count: statusMap.defaulted || 0 },
    { label: 'Completed', count: statusMap.completed || 0 },
  ];

  // Recovery vs Outstanding
  const recoveryBars = [
    { label: 'Recovered', value: stats?.total_recovered || 0 },
    { label: 'Outstanding', value: stats?.outstanding_amount || 0 },
    { label: 'Monthly Due', value: stats?.monthly_recoverable || 0 },
  ];

  // Finance area — cash vs bank vs profit
  const financeSeries = [
    { name: 'Cash', value: finance?.cash_balance || 0 },
    { name: 'Bank', value: finance?.bank_balance || 0 },
    { name: 'Profit', value: finance?.net_profit || 0 },
    { name: 'Expenses', value: finance?.total_expenses || 0 },
  ];

  const statCards = [
    { title: 'Total Customers', value: stats?.total_customers || 0, icon: Users, color: 'text-blue-500', testId: 'stat-total-customers' },
    { title: 'Total Inventory', value: stats?.total_inventory || 0, icon: Package, color: 'text-purple-500', testId: 'stat-total-inventory' },
    { title: 'Total Investment', value: `PKR ${(stats?.total_investment || 0).toLocaleString()}`, icon: DollarSign, color: 'text-amber-500', testId: 'stat-total-investment' },
    { title: 'Total Sales', value: `PKR ${(stats?.total_sales || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'stat-total-sales' },
    { title: 'Total Profit', value: `PKR ${(stats?.total_profit || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-green-500', testId: 'stat-total-profit' },
    { title: 'Monthly Recoverable', value: `PKR ${(stats?.monthly_recoverable || 0).toLocaleString()}`, icon: CreditCard, color: 'text-cyan-500', testId: 'stat-monthly-recoverable' },
    { title: 'Monthly Pending Recovery', value: `PKR ${(stats?.monthly_pending_recovery || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-orange-500', testId: 'stat-monthly-pending-recovery' },
    { title: 'Total Recovered', value: `PKR ${(stats?.total_recovered || 0).toLocaleString()}`, icon: Wallet, color: 'text-emerald-500', testId: 'stat-total-recovered' },
    { title: 'Outstanding', value: `PKR ${(stats?.outstanding_amount || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-yellow-500', testId: 'stat-outstanding' },
    { title: 'Default Customers', value: stats?.default_customers || 0, icon: AlertCircle, color: 'text-red-500', testId: 'stat-default-customers' },
    { title: 'Default Amount', value: `PKR ${(stats?.default_amount || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-red-500', testId: 'stat-default-amount' },
    { title: 'Branches', value: stats?.total_branches || 0, icon: Building2, color: 'text-indigo-500', testId: 'stat-total-branches' },
    { title: 'Employees', value: stats?.total_employees || 0, icon: UserCheck, color: 'text-blue-500', testId: 'stat-total-employees' },
  ];

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <GreetingHeader
        testId="sa-greeting"
        fallbackName="Super Admin"
        subtitle="Live overview — MANHA DIGITAL ERP"
      />

      {/* Business Profit — prominent highlighted card, Super Admin only */}
      <Card
        className="bg-gradient-to-r from-emerald-900/60 via-emerald-800/40 to-amber-900/40 border-emerald-500/40 shadow-lg"
        data-testid="stat-business-profit"
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-emerald-300 text-sm font-medium tracking-wide uppercase">
              Business Profit (All Agreements)
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Sum of business charges across {profit?.total_agreements || 0} agreements
              {profit?.business_charge_percent_avg
                ? ` • avg ${profit.business_charge_percent_avg}%`
                : ''}
            </p>
          </div>
          <TrendingUp className="h-6 w-6 text-emerald-400" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold text-emerald-300" data-testid="business-profit-value">
            PKR {(profit?.business_profit || 0).toLocaleString()}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
            <div>
              <div className="text-slate-400">Total Billed</div>
              <div className="text-white font-semibold">PKR {(profit?.total_billed || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400">Total Sale Price</div>
              <div className="text-white font-semibold">PKR {(profit?.total_sale_price || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400">Collected</div>
              <div className="text-white font-semibold">PKR {(profit?.total_collected || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400">Agreements</div>
              <div className="text-white font-semibold">{profit?.total_agreements || 0}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Net Profit Breakdown — driven by filters below */}
      <Card className="bg-slate-900 border-slate-800" data-testid="net-profit-panel">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-end gap-2 justify-between">
            <div>
              <CardTitle className="text-white text-base">Net Profit Breakdown</CardTitle>
              <p className="text-xs text-slate-400 mt-1">
                Gross Profit + Other Income − Expenses − Commissions Paid − Salaries Paid − Investor Profit
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                data-testid="filter-date-from" className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white" />
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                data-testid="filter-date-to" className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white" />
              <select value={filters.branch_id} onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
                data-testid="filter-branch-id" className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white">
                <option value="">All Branches</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <select value={filters.product_type} onChange={(e) => setFilters({ ...filters, product_type: e.target.value })}
                data-testid="filter-product-type" className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white">
                <option value="">All Categories</option>
                {['Mobile', 'Laptop', 'Vehicle', 'Property', 'Electronics', 'Other'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filters.sales_executive_id} onChange={(e) => setFilters({ ...filters, sales_executive_id: e.target.value })}
                data-testid="filter-sales-executive" className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white">
                <option value="">All Sales Execs</option>
                {salesExecs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button onClick={applyProfitFilters} data-testid="apply-profit-filters"
                className="bg-emerald-600 hover:bg-emerald-700 px-3 py-1 rounded text-white">Apply</button>
              <button onClick={resetProfitFilters} data-testid="reset-profit-filters"
                className="bg-slate-700 hover:bg-slate-600 px-3 py-1 rounded text-white">Reset</button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
            <div className="bg-emerald-900/30 border border-emerald-500/30 rounded p-2">
              <div className="text-emerald-300 uppercase tracking-wide">Gross Profit</div>
              <div className="text-white text-lg font-bold mt-1" data-testid="np-gross">PKR {(profit?.gross_profit || 0).toLocaleString()}</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded p-2">
              <div className="text-slate-400 uppercase tracking-wide">+ Income</div>
              <div className="text-emerald-400 text-lg font-bold mt-1">PKR {(profit?.total_income || 0).toLocaleString()}</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded p-2">
              <div className="text-slate-400 uppercase tracking-wide">− Expenses</div>
              <div className="text-red-400 text-lg font-bold mt-1" data-testid="np-expenses">PKR {(profit?.total_expenses || 0).toLocaleString()}</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded p-2">
              <div className="text-slate-400 uppercase tracking-wide">− Commissions</div>
              <div className="text-red-400 text-lg font-bold mt-1" data-testid="np-commissions">PKR {(profit?.total_commissions_paid || 0).toLocaleString()}</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded p-2">
              <div className="text-slate-400 uppercase tracking-wide">− Salaries</div>
              <div className="text-red-400 text-lg font-bold mt-1" data-testid="np-salaries">PKR {(profit?.total_salaries_paid || 0).toLocaleString()}</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded p-2">
              <div className="text-slate-400 uppercase tracking-wide">− Investor Profit</div>
              <div className="text-red-400 text-lg font-bold mt-1" data-testid="np-investor">PKR {(profit?.total_investor_profit || 0).toLocaleString()}</div>
            </div>
            <div className="bg-amber-900/30 border border-amber-500/40 rounded p-2">
              <div className="text-amber-300 uppercase tracking-wide">= Net Profit</div>
              <div className={`text-lg font-bold mt-1 ${(profit?.net_profit || 0) >= 0 ? 'text-amber-300' : 'text-red-400'}`}
                data-testid="np-net">PKR {(profit?.net_profit || 0).toLocaleString()}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid={s.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">{s.title}</CardTitle>
              <s.icon className={`h-4 w-4 ${s.color}`} />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold text-white">{s.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-slate-900 border-slate-800" data-testid="chart-collections">
          <CardHeader><CardTitle className="text-white">Daily Collections (last 30 days)</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={collectionSeries}>
                <defs><linearGradient id="gCol" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#22C55E" stopOpacity={0.05} />
                </linearGradient></defs>
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <Tooltip {...chartTooltip} />
                <Area type="monotone" dataKey="collected" stroke="#22C55E" fill="url(#gCol)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800" data-testid="chart-recovery">
          <CardHeader><CardTitle className="text-white">Recovery vs Outstanding vs Monthly Due</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={recoveryBars}>
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <Tooltip {...chartTooltip} />
                <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800" data-testid="chart-overdue">
          <CardHeader><CardTitle className="text-white">Installments by Status</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={overdueBars}>
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <Tooltip {...chartTooltip} />
                <Bar dataKey="count" fill="#22C55E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800" data-testid="chart-portfolio">
          <CardHeader><CardTitle className="text-white">Inventory Portfolio Mix</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip {...chartTooltip} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#94A3B8' }} />
                <Pie data={portfolio} dataKey="value" nameKey="name" outerRadius={90} label>
                  {portfolio.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 lg:col-span-2" data-testid="chart-finance">
          <CardHeader><CardTitle className="text-white">Finance Snapshot</CardTitle></CardHeader>
          <CardContent style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={financeSeries} layout="vertical">
                <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} />
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <Tooltip {...chartTooltip} />
                <Bar dataKey="value" fill="#22C55E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    <InstallmentSummaryCards />
    <UpcomingDueCustomers />

    </div>
  );
}
